import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Plus, Minus, Trash2, ShoppingBag, X, Printer } from "lucide-react";
import { brl } from "@/lib/format";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { printOrder } from "@/lib/print";
import { TablesBoard } from "@/components/Tables";

const CHANNELS = [
  { id: "balcao", label: "Balcão" },
  { id: "delivery", label: "Delivery" },
  { id: "retirada", label: "Retirada" },
  { id: "mesa", label: "Mesa" },
];
const PAYMENTS = ["dinheiro", "pix", "debito", "credito", "fiado"];

export default function POS() {
  const [prods, setProds] = useState([]);
  const [cats, setCats] = useState([]);
  const [q, setQ] = useState("");
  const [activeCat, setActiveCat] = useState("all");
  const [cart, setCart] = useState([]);
  const [channel, setChannel] = useState("balcao");
  const [payment, setPayment] = useState("dinheiro");
  const [customer, setCustomer] = useState({ name: "", phone: "", address: "" });
  const [table, setTable] = useState("");
  const [view, setView] = useState("venda");
  const [addingTo, setAddingTo] = useState(null);
  const { restaurant } = useAuth();
  const [autoPrint, setAutoPrint] = useState(() => localStorage.getItem("pg_pos_autoprint") === "1");
  const toggleAutoPrint = (v) => { setAutoPrint(v); localStorage.setItem("pg_pos_autoprint", v ? "1" : "0"); };

  useEffect(() => {
    Promise.all([api.get("/products"), api.get("/categories")]).then(([p, c]) => {
      setProds(p.data.filter((x) => x.active !== false)); setCats(c.data);
    });
  }, []);

  const filtered = useMemo(() => prods.filter((p) => {
    if (activeCat !== "all" && p.category_id !== activeCat) return false;
    if (q && !p.name.toLowerCase().includes(q.toLowerCase())) return false;
    return true;
  }), [prods, q, activeCat]);

  const add = (p) => setCart((c) => {
    const ex = c.find((i) => i.product_id === p.id);
    if (ex) return c.map((i) => i.product_id === p.id ? { ...i, quantity: i.quantity + 1 } : i);
    return [...c, { product_id: p.id, name: p.name, price: p.price, quantity: 1, notes: "" }];
  });
  const upd = (id, delta) => setCart((c) => c.map((i) => i.product_id === id ? { ...i, quantity: Math.max(0, i.quantity + delta) } : i).filter((i) => i.quantity > 0));
  const rm = (id) => setCart((c) => c.filter((i) => i.product_id !== id));
  const subtotal = cart.reduce((s, i) => s + i.price * i.quantity, 0);
  const total = subtotal;

  const finalize = async () => {
    if (cart.length === 0) return toast.error("Carrinho vazio");
    if (channel === "mesa" && !table.trim()) return toast.error("Informe o número da mesa");
    try {
      if (addingTo) {
        await api.post(`/orders/${addingTo.orders[addingTo.orders.length - 1].id}/items`, { items: cart });
        toast.success(`Itens adicionados à ${addingTo.table}`); setAddingTo(null); setCart([]); setView("mesas"); return;
      }
      const r = await api.post("/orders", {
        channel, items: cart, payment_method: channel === "mesa" ? "pendente" : payment,
        customer_name: channel === "mesa" ? `Mesa ${table}` : customer.name, customer_phone: customer.phone, address: customer.address,
        table_id: channel === "mesa" ? `Mesa ${table}` : null,
      });
      if (autoPrint) printOrder(r.data, restaurant);
      if (channel === "mesa") { toast.success(`Mesa ${table} aberta — pedido enviado à cozinha`); setView("mesas"); }
      else toast.success("Venda concluída no PDV!");
      setCart([]); setCustomer({ name: "", phone: "", address: "" }); setTable("");
    } catch (e) { toast.error(e.response?.data?.detail?.message || e.response?.data?.detail || "Erro"); }
  };
  const startAddItems = (t) => { setAddingTo(t); setChannel("mesa"); setTable(t.table.replace(/^Mesa\s*/i, "")); setView("venda"); toast.info(`Adicionando itens à ${t.table}`); };

  return (
    <div className="grid lg:grid-cols-12 gap-6" data-testid="pos-page">
      <div className="lg:col-span-8 space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3"><h1 className="font-display text-3xl font-extrabold text-stone-900">PDV</h1>
            <div className="flex rounded-lg bg-stone-100 p-0.5 text-xs font-semibold">{[["venda", "Venda"], ["mesas", "Mesas ocupadas"]].map(([k, l]) => <button key={k} onClick={() => setView(k)} className={`px-3 py-1.5 rounded-md ${view === k ? "bg-white shadow text-stone-900" : "text-stone-500"}`} data-testid={`pos-view-${k}`}>{l}</button>)}</div></div>
          <div className="flex items-center gap-2 flex-wrap">
            <label className="flex items-center gap-1.5 text-xs text-stone-600 mr-2"><input type="checkbox" checked={autoPrint} onChange={(e) => toggleAutoPrint(e.target.checked)} data-testid="pos-autoprint" /><Printer className="h-3.5 w-3.5" /> Imprimir ao lançar</label>
            {CHANNELS.map((c) => (
            <Button key={c.id} size="sm" variant={channel === c.id ? "default" : "outline"} onClick={() => setChannel(c.id)} className={channel === c.id ? "bg-orange-600 hover:bg-orange-700" : ""} data-testid={`channel-${c.id}`}>{c.label}</Button>
          ))}</div>
        </div>
        {view === "mesas" ? <TablesBoard onAddItems={startAddItems} products={prods} /> : <>
        {addingTo && <div className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-sm flex justify-between items-center" data-testid="pos-adding-banner"><span>Adicionando itens à <strong>{addingTo.table}</strong></span><button className="text-xs underline" onClick={() => setAddingTo(null)}>cancelar</button></div>}
        <Input placeholder="Buscar produto..." value={q} onChange={(e) => setQ(e.target.value)} data-testid="pos-search" />
        <div className="flex gap-2 overflow-x-auto no-scrollbar pb-2">
          <Button size="sm" variant={activeCat === "all" ? "default" : "outline"} onClick={() => setActiveCat("all")} className={activeCat === "all" ? "bg-stone-900" : ""}>Todos</Button>
          {cats.map((c) => (
            <Button key={c.id} size="sm" variant={activeCat === c.id ? "default" : "outline"} onClick={() => setActiveCat(c.id)} className={activeCat === c.id ? "bg-stone-900" : ""}>{c.name}</Button>
          ))}
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {filtered.map((p) => (
            <button key={p.id} onClick={() => add(p)} data-testid={`pos-product-${p.id}`} className="text-left rounded-xl border border-stone-200 bg-white p-4 hover:border-orange-400 hover:shadow-md transition">
              <div className="font-medium text-stone-900 line-clamp-2">{p.name}</div>
              <div className="mt-2 font-mono-num font-bold text-orange-600">{brl(p.price)}</div>
            </button>
          ))}
        </div>
        </>}
      </div>
      {view === "venda" && <div className="lg:col-span-4 lg:sticky lg:top-8 self-start">
        <Card className="rounded-2xl">
          <CardContent className="p-6 space-y-4">
            <div className="flex items-center gap-2"><ShoppingBag className="h-5 w-5 text-orange-600" /><h3 className="font-display font-bold text-lg">Comanda</h3>
              <Badge variant="secondary" className="ml-auto">{cart.length} itens</Badge></div>
            <div className="max-h-64 overflow-y-auto space-y-2">
              {cart.length === 0 && <p className="text-sm text-stone-500 py-6 text-center">Adicione produtos</p>}
              {cart.map((i) => (
                <div key={i.product_id} className="flex items-center gap-2 rounded-lg border border-stone-100 p-2" data-testid={`cart-item-${i.product_id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{i.name}</div>
                    <div className="text-xs text-stone-500 font-mono-num">{brl(i.price)}</div>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button size="icon" variant="outline" className="h-7 w-7" onClick={() => upd(i.product_id, -1)}><Minus className="h-3 w-3" /></Button>
                    <span className="w-6 text-center font-mono-num">{i.quantity}</span>
                    <Button size="icon" variant="outline" className="h-7 w-7" onClick={() => upd(i.product_id, 1)}><Plus className="h-3 w-3" /></Button>
                    <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => rm(i.product_id)}><X className="h-3 w-3" /></Button>
                  </div>
                </div>
              ))}
            </div>
            {channel === "mesa" && <div className="pt-2 border-t"><Input placeholder="Número da mesa *" inputMode="numeric" value={table} onChange={(e) => setTable(e.target.value)} className="h-11 font-bold" data-testid="pos-table" /><p className="text-xs text-stone-500 mt-1">A mesa fica aberta até você fechar a conta em "Mesas ocupadas".</p></div>}
            {channel !== "balcao" && channel !== "mesa" && (
              <div className="space-y-2 pt-2 border-t">
                <Input placeholder="Nome do cliente" value={customer.name} onChange={(e) => setCustomer({ ...customer, name: e.target.value })} data-testid="pos-customer-name" />
                <Input placeholder="Telefone" value={customer.phone} onChange={(e) => setCustomer({ ...customer, phone: e.target.value })} data-testid="pos-customer-phone" />
                {channel === "delivery" && <Input placeholder="Endereço" value={customer.address} onChange={(e) => setCustomer({ ...customer, address: e.target.value })} data-testid="pos-customer-address" />}
              </div>
            )}
            {channel !== "mesa" && <div>
              <label className="text-xs text-stone-500 uppercase tracking-wider font-semibold">Pagamento</label>
              <select className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm mt-1 capitalize" value={payment} onChange={(e) => setPayment(e.target.value)} data-testid="pos-payment">
                {PAYMENTS.map((p) => <option key={p} value={p} className="capitalize">{p}</option>)}
              </select>
            </div>}
            <div className="flex items-baseline justify-between pt-2 border-t">
              <span className="text-stone-500">Total</span>
              <span className="font-mono-num text-2xl font-extrabold text-stone-900">{brl(total)}</span>
            </div>
            <Button className="w-full bg-orange-600 hover:bg-orange-700 h-12" onClick={finalize} data-testid="pos-finalize">{addingTo ? "Adicionar à mesa" : channel === "mesa" ? "Abrir mesa / enviar à cozinha" : "Finalizar venda"}</Button>
          </CardContent>
        </Card>
      </div>}
    </div>
  );
}
